/**
 * ResponseContent Component - Clean markdown display with numbered citations
 *
 * Phase 2: Content & Citations - Simplified content rendering
 *
 * Features:
 * - Clean markdown rendering without complex interactions
 * - Numbered citation system (¹ ² ³) replacing inline complexity
 * - Simple native browser tooltips for citations (no React overlays)
 * - Focus on readability and proper typography
 * - Agent Squad Native citation integration
 *
 * User Feedback Incorporated:
 * - Include page numbers in citation tooltips
 * - Simple tooltip approach (no complex overlays)
 * - Remove layout disruption from inline citations
 */

import React from 'react';
import { Message } from '../types/chat';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

interface ResponseContentProps {
  message: Message;
}

export const ResponseContent: React.FC<ResponseContentProps> = ({
  message,
}) => {
  // Custom markdown components for clean rendering
  const markdownComponents = {
    p: ({ children }: any) => (
      <p className="mb-3 leading-relaxed text-gray-800">{children}</p>
    ),
    ul: ({ children }: any) => (
      <ul className="mb-3 ml-4 space-y-1 list-disc text-gray-800">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="mb-3 ml-4 space-y-1 list-decimal text-gray-800">
        {children}
      </ol>
    ),
    li: ({ children }: any) => <li className="text-gray-800">{children}</li>,
    h1: ({ children }: any) => (
      <h1 className="text-xl font-semibold mb-3 text-gray-900">{children}</h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="text-lg font-semibold mb-2 text-gray-900">{children}</h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-base font-semibold mb-2 text-gray-900">{children}</h3>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }: any) => (
      <em className="italic text-gray-800">{children}</em>
    ),
    table: ({ children }: any) => (
      <div className="mb-3 text-sm text-gray-700">
        <div className="font-medium mb-1">Data table:</div>
        <div className="bg-gray-50 p-2 rounded text-xs">{children}</div>
      </div>
    ),
    a: ({ href, children }: any) => (
      <a
        href={href}
        className="text-blue-600 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    text: ({ children }: any) => children,
  };
  const processedContent = message.content;

  return (
    <div className="response-content w-full">
      {/* Main content with clean markdown */}
      <div className="prose prose-sm max-w-full break-words overflow-wrap-anywhere">
        <ReactMarkdown
          components={markdownComponents}
          rehypePlugins={[rehypeRaw]}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};
